import { ID_OPTIONS } from '@rogoag/shared-ts';
import _ from 'lodash';
import type shp from 'shpjs';
import type { MultiPolygon, Point, Polygon, Position, Feature, GeoJsonProperties, Geometry } from 'geojson';
import { Job, Mission, Sample, SampleSite, SampleZone, SamplingTypes, SoilCore, Zone, ZoneType } from '../../../../db';
import { ConfirmColumn, RogoShapefileType } from '../../../../types/types';
import { alertError, alertWarn } from '../../../../alertDispatcher';
import { processMultipolygonCoords } from '../../../BoundaryService';
import { normalizeDoubleEncodedJsonString } from '../../../../utils';

class OriginalPointsOrZonesFileProcessor {
  private job: Job;
  private skipFile: boolean;
  private noMatchCentroids: string[];
  private duplicateCentroids: string[];
  private noMatchCores: string[];
  private noMatchPolygons: string[];
  private allPointNames: string[];

  constructor(
    private shpDataArray: shp.FeatureCollectionWithFilename[],
    private confirmColumn: (item: ConfirmColumn) => Promise<string>,
    private shpfile: {
      data: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[];
      mode: RogoShapefileType;
      source: string;
      name: string;
    },
    private errorMessages: string[],
    private mission: Mission,
  ) {
    const job = this.mission.getJob();

    if (!job) {
      throw 'Mission job not found';
    }

    this.job = job;
    this.skipFile = false;
    this.noMatchCentroids = [];
    this.duplicateCentroids = [];
    this.noMatchCores = [];
    this.noMatchPolygons = [];
    this.allPointNames = [];
  }

  async process() {
    this.noMatchCentroids = [];
    this.duplicateCentroids = [];
    this.noMatchCores = [];
    this.noMatchPolygons = [];
    this.allPointNames = [];

    const useOriginalSampleId = this.job.use_original_sample_id;

    for (const shpdata of this.shpDataArray) {
      // TODO could we process these in parallel?
      await this.processShapeData(shpdata);

      if (this.skipFile) {
        break;
      }
    }

    if (this.noMatchPolygons.length > 0) {
      alertWarn(
        `The following polygons did not match our naming conventions - please, rename them: ${this.noMatchPolygons.join(', ')}`,
      );
    }

    if (this.noMatchCentroids.length > 0) {
      alertWarn(
        `The following centroids did not match our naming conventions${useOriginalSampleId ? '' : ` - we will rename them because "Use Original Sample ID" flag is off`}: ${this.noMatchCentroids.join(', ')}`,
      );
    }

    if (this.duplicateCentroids.length > 0) {
      alertWarn(
        `The following centroids have duplicates${useOriginalSampleId ? '' : ` - we will rename them because "Use Original Sample ID" flag is off`}: ${this.duplicateCentroids.join(', ')}`,
      );
    }

    if (this.noMatchCores.length > 0) {
      alertWarn(
        `The following cores did not match our naming conventions - we will try to geomatch and rename them: ${this.noMatchCores.join(', ')}`,
      );
    }

    const duplicates = _.uniq(this.allPointNames.filter((item, index) => this.allPointNames.includes(item, index + 1)));

    if (duplicates.length > 0) {
      alertWarn(`The following points have duplicates and will be renamed: ${duplicates.join(', ')}`);
    }

    if (this.errorMessages.length > 0) {
      alertWarn(this.errorMessages.join('; '));
    }
  }

  private getFeatureNameFromProperties(feature: Feature<Geometry, GeoJsonProperties>, sampleIDProp: string): string {
    if (!feature.properties || !feature.properties[sampleIDProp]) {
      return '';
    }

    return feature.properties[sampleIDProp].toString().trim();
  }

  private async processShapeData(shpdata: shp.FeatureCollectionWithFilename) {
    if (shpdata.type !== 'FeatureCollection') {
      console.error('Shapefile does not contain a feature collection', shpdata);
      this.errorMessages.push(`Shapefile does not contain a feature collection: ${shpdata.fileName}.shp`);

      return;
    }

    const columnHeaders = [
      ...shpdata.features.reduce<Set<string>>((acc, feature) => {
        Object.keys(feature.properties || {}).forEach((key) => acc.add(key));

        return acc;
      }, new Set([])),
    ];

    // confirm selection with user
    const sampleIDProp: string | undefined = await this.chooseSampleIDProp(columnHeaders, shpdata);

    if (!sampleIDProp) {
      console.error('No sample IDs found', shpdata);
      this.errorMessages.push(`No sample IDs found: ${shpdata.fileName}.shp`);

      return;
    }

    if (sampleIDProp === 'ignore') {
      return;
    }

    const externalCustomerID = this.mission.getJob()?.external_reference_id_column;

    if (this.mixedPointTypesPresent(shpdata.features, sampleIDProp)) {
      this.errorMessages.push('Mixed points types.');

      // We no longer want to skip the file if a client
      // supplied bad core ids for the zone mission case.
      if (!this.mission.is_zone_mission()) {
        this.skipFile = true;

        return;
      }
    }

    for (const feature of shpdata.features) {
      const featureName: string = this.getFeatureNameFromProperties(feature, sampleIDProp);

      if (!feature.geometry) {
        console.error('Feature has no geometry', feature);
        this.errorMessages.push('Feature has no geometry');

        return;
      }

      // TODO this is specifically used for Brookside for now for BCS jobs. Should make this more adapatable
      if (feature.properties && 'GrowerID' in feature.properties) {
        this.job.grower_id = feature.properties['GrowerID'];
      }

      if (this.isPoint(feature.geometry)) {
        this.processPoint(feature, featureName, externalCustomerID);
      } else if (this.isPolygonOrMultiPolygon(feature.geometry)) {
        await this.processPolygonOrMultipolygon(feature, featureName, externalCustomerID);
      } else {
        console.error('Feature has invalid geometry type for samples', feature);
        this.errorMessages.push(`Feature has invalid geometry type for boundaries: ${feature.geometry.type}`);
      }
    }
  }

  private async processPolygonOrMultipolygon(
    feature: Feature<Geometry, GeoJsonProperties>,
    featureName: string,
    externalCustomerIDColumn?: string,
  ) {
    let geomCoordinates: Position[][][];

    if (this.isPolygon(feature.geometry)) {
      geomCoordinates = [feature.geometry.coordinates];
    } else {
      geomCoordinates = (feature.geometry as MultiPolygon).coordinates;
    }

    if (featureName.endsWith('.0')) {
      featureName = featureName.slice(0, -2);
    }

    let sampleZoneName = featureName;
    let zone: Zone | undefined;

    if (this.mission.is_zone_mission()) {
      const match = featureName ? matchesCentroid(featureName) : undefined;

      if (!match) {
        this.noMatchPolygons.push(featureName ? featureName : geomCoordinates.toString());
      }

      if (!sampleZoneName) {
        sampleZoneName = this.mission.getNextAvailableUndefinedSampleId();
      }

      zone = this.mission.getZones().find((sel) => sel.zone_name === sampleZoneName);

      if (!zone) {
        zone = this.createSampleZone(sampleZoneName, feature, externalCustomerIDColumn);
      }
    } else {
      // For a grid mission, we want to just create zones for visualization.
      // So, their name should not equal a feature name, so that it won't get
      // attached to a sample.
      const visualZoneName = Zone.makeVisualZoneName(sampleZoneName);

      zone = this.mission.getZones().find((sel) => sel.zone_name === visualZoneName);

      if (!zone) {
        zone = this.createVisualZone(visualZoneName);
      }
    }

    if (!zone) {
      return;
    }

    const processingErrors = await processMultipolygonCoords(this.mission, geomCoordinates, zone);

    if (!zone.hasValidBoundaries()) {
      this.errorMessages.push(`Zone ${zone.zone_name} has invalid boundaries`);
    }

    processingErrors.forEach((error: string) => this.errorMessages.push(error));
  }

  private createVisualZone(featureName: string) {
    const zone = Zone.create();
    zone.Mission_id = this.mission.instance_id;
    zone.zone_name = featureName;
    zone.zone_type = ZoneType.VISUAL_ZONE;

    return zone;
  }

  private createSampleZone(
    featureName: string,
    feature: Feature<Geometry, GeoJsonProperties>,
    externalCustomerIDColumn?: string,
  ) {
    const zone = Zone.create();
    zone.Mission_id = this.mission.instance_id;
    zone.zone_name = featureName;
    zone.zone_type = ZoneType.SAMPLE_ZONE;

    let sampleSite = SampleSite.findByOriginalSampleID(this.mission, featureName);

    if (!sampleSite) {
      sampleSite = this.mission.createSampleSite(featureName);
      sampleSite.sample_site_source = 'Customer';

      sampleSite.external_reference_id = this.validateExternalReferenceID(
        externalCustomerIDColumn,
        feature,
        featureName,
      );

      // if the geojson properties has a ZoneID property, save that as the original sample id
      if (feature.properties && feature.properties['ZoneID']) {
        sampleSite.external_reference_id = feature.properties['ZoneID'].toString();
      }
    }

    let sampleZone = sampleSite.getSampleZone();

    if (!sampleZone) {
      sampleZone = SampleZone.create();
      sampleZone.SampleSite_id = sampleSite.instance_id;
    }

    sampleZone.Zone_id = zone.instance_id;

    return zone;
  }

  private processPoint(
    feature: Feature<Geometry, GeoJsonProperties>,
    featureName: string,
    externalCustomerID?: string,
  ) {
    const geomCoordinates = (feature.geometry as Point).coordinates;

    if (this.mission.is_zone_mission()) {
      // zone sampling
      this.processCore(featureName, geomCoordinates);
    } else {
      // grid sampling
      this.processCentroid(feature, featureName, geomCoordinates, externalCustomerID);
    }
  }

  private processCore(featureName: string, geomCoordinates: Position) {
    const primarySpec = this.mission.getPrimarySpec();

    if (!primarySpec) {
      throw new Error(`Processing core: no primary spec found`);
    }

    this.allPointNames.push(featureName ? featureName : geomCoordinates.toString());

    const { matches, sampleId, coreNumber } = matchCore(featureName);

    if (!matches) {
      this.noMatchCores.push(featureName ? featureName : geomCoordinates.toString());
    }

    const finalSampleId = matches ? sampleId : this.mission.getNextAvailableUndefinedSampleId();

    if (!finalSampleId) {
      throw new Error(`Core feature does not have a sample ID: ${featureName}`);
    }

    SoilCore.createCore(this.mission, finalSampleId, geomCoordinates[1], geomCoordinates[0], 'Customer', coreNumber);
  }

  private validateExternalReferenceID(
    externalCustomerIDColumn: string | undefined,
    feature: Feature<Geometry, GeoJsonProperties>,
    featureName: string,
  ) {
    if (externalCustomerIDColumn) {
      const transformedFeatureProperties = Object.keys(feature.properties || {}).reduce((acc, key) => {
        acc[key.toUpperCase()] = feature.properties ? feature.properties[key] : '';

        return acc;
      }, {});

      const externalReferenceId = transformedFeatureProperties
        ? transformedFeatureProperties[externalCustomerIDColumn.toUpperCase()]
        : '';

      // if we have an externalCustomerID and it's not in the properties, we need to throw an error
      if (!externalReferenceId) {
        throw new Error(`External reference ID not found in properties for sample ${featureName}`);
      }

      return externalReferenceId;
    }
  }

  private processCentroid(
    feature: Feature<Geometry, GeoJsonProperties>,
    featureName: string,
    geomCoordinates: Position,
    externalCustomerIDColumn: string | undefined,
  ) {
    const useOriginalSampleId = this.job.use_original_sample_id;

    // Register no-match centroids.
    const matches = featureName ? matchesCentroid(featureName) : false;

    if (!matches) {
      this.noMatchCentroids.push(featureName ? featureName : geomCoordinates.toString());
    }

    if (!featureName) {
      if (useOriginalSampleId) {
        const errorMessage = `Centroid with coordinates ${geomCoordinates.toString()} has an empty id and "Use Original Sample ID" flag is found on the deal - skipping`;
        this.errorMessages.push(errorMessage);

        return;
      } else {
        featureName = this.mission.getNextAvailableUndefinedSampleId();
      }
    }

    // Do not allow duplicates if "Use Original Sample ID" flag is on.
    const sampleSiteAlreadyExists = SampleSite.findByOriginalSampleID(this.mission, featureName);

    if (sampleSiteAlreadyExists) {
      if (useOriginalSampleId) {
        const errorMessage = `Centroid ${featureName} has a duplicate and "Use Original Sample ID" flag is found on the deal - skipping`;
        this.errorMessages.push(errorMessage);

        return;
      } else {
        this.duplicateCentroids.push(featureName ? featureName : geomCoordinates.toString());

        featureName += `copy${this.mission.getNextSampleNumber() + 100}`;
      }
    }

    const samples = this.mission.getAllSamples();
    const samplingSpecs = this.mission.getSamplingSpecs();
    const samplesToCreate = samplingSpecs.length;

    const isBackup = feature.properties?.isBackup;
    const externalRefID = this.validateExternalReferenceID(externalCustomerIDColumn, feature, featureName);

    if (parseInt(isBackup) === 1) {
      console.log('Adding backup sample');
      const backupSamples = JSON.parse(normalizeDoubleEncodedJsonString(this.mission?.backup_samples)) || [];

      backupSamples.push({
        ext_ref_id: externalRefID,
        lat: geomCoordinates[1],
        lon: geomCoordinates[0],
        sample_id: featureName,
        getCoordinates: () => {
          return [geomCoordinates[0], geomCoordinates[1]];
        },
      });

      this.mission.backup_samples = JSON.stringify(backupSamples);

      return;
    }

    let i = 0;

    while (i < samplesToCreate) {
      const sample = Sample.create();
      // If useOriginalSampleId is true, then we're going set the sample id to a sequence number
      // for our convenience. This flag usually means, that client's ids are too long
      // and cumbersome to use. That's why we're using sequence numbers instead.
      const sampleId = useOriginalSampleId ? (samples.length + 1 + i).toString() : featureName;
      sample.sample_id = sampleId;

      let sampleSite = this.mission.getSampleSites().find((sel) => sel.original_sample_id === featureName);

      if (!sampleSite) {
        sampleSite = SampleSite.createWithCentroid(
          this.mission,
          featureName,
          geomCoordinates[1],
          geomCoordinates[0],
          externalRefID,
        );
        sampleSite.sample_site_source = 'Customer';
      }

      sample.SampleSite_id = sampleSite.instance_id;
      sample.sample_type = SamplingTypes.REGULAR;
      sample.SamplingSpec_id = samplingSpecs[i].instance_id;

      i++;
    }
  }

  private mixedPointTypesPresent(features: Feature<Geometry, GeoJsonProperties>[], sampleIDProp: string) {
    const pointTypes = new Set<'centroid' | 'core'>();

    for (const feature of features) {
      const featureName: string = this.getFeatureNameFromProperties(feature, sampleIDProp);

      if (!feature.geometry) {
        continue;
      }

      if (!this.isPoint(feature.geometry)) {
        continue;
      }

      if (matchCore(featureName).matches) {
        pointTypes.add('core');
      } else {
        pointTypes.add('centroid');
      }

      if (pointTypes.size > 1) {
        return true;
      }
    }

    return false;
  }

  private async chooseSampleIDProp(columnHeaders: string[], shpdata: shp.FeatureCollectionWithFilename) {
    if (columnHeaders.length <= 1) {
      // TODO technically this doesn't guarantee that there's only one column, just selects the first column...
      // if theres only one column, that must be the sample IDs
      return columnHeaders[0];
    }

    let sampleIDProp: string | undefined = undefined;

    // If wanting to test a mission with points that need selected, comment out this for loop
    for (const opt of ID_OPTIONS) {
      const prop = columnHeaders.find((header) => {
        return header.toLowerCase().trim() === opt.toLowerCase().trim();
      });

      if (prop) {
        sampleIDProp = prop;
        break;
      }
    }

    sampleIDProp = await this.confirmColumn({
      filename: `${shpdata.fileName}.shp`,
      source: this.shpfile.source,
      selection: sampleIDProp || '',
      headers: columnHeaders,
      // would prefer for this to be inferred better
      data: shpdata.features.map((feature) =>
        columnHeaders.map<string>((header) => {
          const value = feature.properties?.[header];

          return value != null ? value.toString() : '';
        }),
      ),
      featureTypes: shpdata.features.reduce((acc, feature) => {
        acc[feature.geometry.type] = (acc[feature.geometry.type] || 0) + 1;

        return acc;
      }, {}),
    });

    return sampleIDProp;
  }

  private isPoint(geometry: Geometry): geometry is Point {
    return geometry.type === 'Point';
  }

  private isPolygonOrMultiPolygon(geometry: Geometry): geometry is Polygon | MultiPolygon {
    return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
  }

  private isPolygon(geometry: Geometry): geometry is Polygon {
    return geometry.type === 'Polygon';
  }
}

// Do not allow "0" to match.
const CENTROID_REGEX = new RegExp(/^(?!0$)[a-zA-Z0-9]+$/);

export function matchesCentroid(featureName: string): RegExpMatchArray | null {
  if (featureName === '0') {
    return null;
  }

  return featureName.match(CENTROID_REGEX);
}

// Do not allow "0" as a sample id.
// console.log("A0.12".match(CORE_REGEX));
// [
//   "A0.12",        // full match
//   "A0",           // <-- match[2] (first part)
//   ".12",          // match[3] (separator + number)
//   "12"            // match[4] (number only)
// ]
const CORE_REGEX = new RegExp(/^(?!0([,.;-]|$))([a-zA-Z0-9]+)([,.;-]([1-9][0-9]*))+$/);

type CoreMatchResult =
  | { matches: true; sampleId: string; coreNumber: number }
  | { matches: false; sampleId: undefined; coreNumber: undefined };

export function matchCore(featureName: string): CoreMatchResult {
  const match = featureName.match(CORE_REGEX);

  if (match) {
    return {
      matches: true,
      sampleId: match[2],
      coreNumber: parseInt(match[4]),
    };
  }

  return {
    matches: false,
    sampleId: undefined,
    coreNumber: undefined,
  };
}

export default OriginalPointsOrZonesFileProcessor;
